/**
 * React Hooks for Contact Operations
 */

import { useState, useEffect } from 'react';
import { Contact } from '../types/schema';
import * as contactService from '../services/contact.service';

/**
 * Hook to get all contacts for a customer with real-time updates
 */
export function useCustomerContacts(customerId: string | null) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) {
      setContacts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = contactService.subscribeToCustomerContacts(
      customerId,
      (contactsData) => {
        setContacts(contactsData);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [customerId]);

  return { contacts, loading };
}

/**
 * Hook to get a single contact by ID
 */
export function useContact(customerId: string | null, contactId: string | null) {
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!customerId || !contactId) {
      setContact(null);
      setLoading(false);
      return;
    }

    contactService
      .getContact(customerId, contactId)
      .then((data) => {
        setContact(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching contact:', err);
        setError(err);
        setLoading(false);
      });
  }, [customerId, contactId]);

  return { contact, loading, error };
}

/**
 * Hook to get primary contact for a customer
 */
export function usePrimaryContact(customerId: string | null) {
  const [primaryContact, setPrimaryContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!customerId) {
      setPrimaryContact(null);
      setLoading(false);
      return;
    }

    contactService
      .getPrimaryContact(customerId)
      .then((data) => {
        setPrimaryContact(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching primary contact:', err);
        setError(err);
        setLoading(false);
      });
  }, [customerId]);

  return { primaryContact, loading, error };
}

/**
 * Hook to create, update, and delete contacts
 */
export function useContactMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createContact = async (
    customerId: string,
    data: Omit<Contact, 'id' | 'customerId' | 'createdAt' | 'updatedAt'>
  ) => {
    setLoading(true);
    setError(null);
    try {
      const id = await contactService.createContact(customerId, data);
      setLoading(false);
      return id;
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const updateContact = async (
    customerId: string,
    contactId: string,
    data: Partial<Omit<Contact, 'id' | 'customerId' | 'createdAt'>>
  ) => {
    setLoading(true);
    setError(null);
    try {
      await contactService.updateContact(customerId, contactId, data);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const deleteContact = async (customerId: string, contactId: string) => {
    setLoading(true);
    setError(null);
    try {
      await contactService.deleteContact(customerId, contactId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const setPrimaryContact = async (customerId: string, contactId: string) => {
    setLoading(true);
    setError(null);
    try {
      await contactService.setPrimaryContact(customerId, contactId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  return {
    createContact,
    updateContact,
    deleteContact,
    setPrimaryContact,
    loading,
    error,
  };
}
